import {
  Typography,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import { useEffect, useCallback, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useAppAuth } from "../context/AuthContext";
import api, { setAuthToken } from "../utils/axiosInstance";

const AUTO_LOGOUT_TIME = parseInt(
  process.env.REACT_APP_AUTO_LOGOUT_TIME || 15 * 60 * 1000,
  10
);

const Dashboard = () => {
  const { logout, getAccessTokenSilently, user } = useAuth0();
  const { appUser } = useAppAuth();
  const navigate = useNavigate();
  const timerRef = useRef(null);

  const [loadingShop, setLoadingShop] = useState(false);

  // Logout function
  const handleLogout = useCallback(() => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  // Auto logout timer
  useEffect(() => {
    const resetTimer = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleLogout, AUTO_LOGOUT_TIME);
    };

    const activityEvents = [
      "mousemove",
      "mousedown",
      "click",
      "scroll",
      "keypress",
      "touchstart",
    ];

    activityEvents.forEach((event) =>
      window.addEventListener(event, resetTimer)
    );

    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      activityEvents.forEach((event) =>
        window.removeEventListener(event, resetTimer)
      );
    };
  }, [handleLogout]);

  // Automatically redirect sellers to their ShopDashboard
  useEffect(() => {
    const redirectSellerToShop = async () => {
      if (appUser?.role?.toLowerCase() === "seller") {
        try {
          setLoadingShop(true);

          // Get Auth0 access token and attach to axios
          const token = await getAccessTokenSilently();
          setAuthToken(token);

          // Fetch seller's shop
          const res = await api.get("/shops/my");
          if (res.data?.slug) {
            navigate(`/shops/${res.data.slug}/dashboard`, { replace: true });
          } else {
            // No shop yet → navigate to shop creation page
            navigate("/shops/my", { replace: true });
          }
        } catch (err) {
          console.warn(
            "No shop found for seller, redirecting to create shop page"
          );
          navigate("/shops/my", { replace: true });
        } finally {
          setLoadingShop(false);
        }
      }
    };

    redirectSellerToShop();
  }, [appUser, getAccessTokenSilently, navigate]);

  // Admins see the normal dashboard
  if (loadingShop) {
    return (
      <Box display="flex" justifyContent="center" mt={{ xs: 2, sm: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (appUser?.role?.toLowerCase() === "seller") {
    // While redirecting, show a placeholder
    return (
      <Box p={{ xs: 2, sm: 3 }}>
        <Typography variant="h6">Redirecting to your shop...</Typography>
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      {/* Header with Logout button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Welcome to Wansight Adminboard</Typography>
        <Button variant="outlined" color="error" onClick={handleLogout}>
          Logout
        </Button>
      </Box>

      <Typography variant="subtitle1" mb={3}>
        Manage Items, Services, Jobs, Airbnbs, and Shops
      </Typography>

      {user && (
        <Typography
          variant="body1"
          mb={2}
          sx={{ wordBreak: "break-word" }} // ✅ prevents overflow on mobile
        >
          Logged in as: <strong>{user.name || user.email}</strong>
        </Typography>
      )}
    </Box>
  );
};

export default Dashboard;
